import { SxProps } from '@mui/system';
import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { CcfTranslationKey } from '@nice-ccf/i18n';
import PropTypes from 'prop-types';
import { useTranslator } from '../../ccf-translator/ccf-translator';
import { visuallyHidden } from '@mui/utils';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';

// /**
// * component function for rendering table component
// * @example ' <CcfTable />'
// */
// function stableSort(array:any, comparator:any) {
//   const stabilizedThis = array.map((el:any, index:any) => [el, index])
//   stabilizedThis.sort((a:any, b:any) => {
//     const order = comparator(a[0], b[0])
//     if (order !== 0) {
//       return order
//     }
//     return a[1] - b[1]
//   })
//   return stabilizedThis.map((el:any) => el[0])
// }

/**
 * component function for rendering table component
 * @example ' <CcfTable />'
 */
export function CcfTableSortableHeader(props: any) {
  const {
    headCells,
    order,
    orderBy,
    onRequestSort,
    headerStyle,
    isHideSortIcon,
  } = props;

  /**
   * component function for rendering table component
   * @example ' <CcfTable />'
   */
  const createSortHandler = (property: any) => (event: any) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead >
      <TableRow sx={headerStyle}>
        {headCells.map((headCell: any) => (
          <TableCell
            key={headCell}
            align="left"
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell ? order : false}
          >
            <TableSortLabel
              direction={orderBy === headCell ? order : 'asc'}
              onClick={createSortHandler(headCell)}
              hideSortIcon={isHideSortIcon}
            >
              {headCell}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export interface RowDataStructure {
  key?: number;
  data: any;
}
/**
 * basic structure for accepatable props for CcfSortItems component
 */
export interface CcfTableProps {
  headersData?: string[];
  rowsData?: RowDataStructure[];
  colWidths?: string[];
  headerStyle?: SxProps;
  rowStyle?: SxProps;
  tableStyle?: SxProps;
  containerStyle?: SxProps;
  onRowClick?: (arg: any) => void;
  isHideSortIcon?: boolean;
}

/**
 * component function for rendering table component
 * @example ' <CcfTable />'
 */
export function CcfTable({
  headerStyle,
  headersData,
  rowStyle,
  rowsData,
  tableStyle,
  colWidths,
  containerStyle,
  onRowClick,
  isHideSortIcon,
}: CcfTableProps) {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('CHANNEL');
  const [translate] = useTranslator();
  /**
   * function to handle row click event
   * @example rowClick()
   */
  const handleRequestSort = (event: any, property: any) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * component function for rendering table component
   * @example ' <CcfTable />'
   */
  function descendingComparator(a: any, b: any, orderBy: any) {
    switch (orderBy) {
      case 'CHANNEL':
        orderBy = 'channel';
        break;
      case 'CONTACTS IN QUEUE':
        orderBy = 'totalcontacts';
        break;
      case 'WAIT':
        orderBy = 'longestwait';
        break;
      case 'AGENT STATES':
        orderBy = 'agentStates';
        break;
      case 'LONGEST WAIT':
        orderBy = 'longestWait';
        break;
      case 'IN QUEUE':
        orderBy = 'inQueue';
        break;
      case 'SKILL NAME':
        orderBy = 'skillname';
        break;
    }
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  /**
   * component function for rendering table component
   * @example ' <CcfTable />'
   */
  function getComparator(order: any, orderBy: any) {
    return order === 'desc'
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy);
  }

  /**
   * function to handle row click event
   * @example rowClick()
   */
  const rowClick = (data: any) => {
    if (onRowClick) {
      onRowClick(data);
    }
  };
  return (
    <TableContainer sx={containerStyle}>
      <Table sx={tableStyle} stickyHeader aria-label="customized table">
        {colWidths ? (
          <colgroup>
            {colWidths?.map((colWidth, i) => (
              <col key={'colWidth_' + i} width={colWidth} />
            ))}
          </colgroup>
        ) : (
          ''
        )}
        {/* <TableHead >
          <TableRow sx={headerStyle}>
            {headersData?.map((header)=>(
              <TableCell aria-label={header} >{header}</TableCell>
            ))}
          </TableRow>
        </TableHead> */}

        <CcfTableSortableHeader
        headerStyle={headerStyle}
          headCells={headersData}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          rowCount={rowsData?.length || 0}
          isHideSortIcon={isHideSortIcon}
        />
        <TableBody>
          {rowsData
            ?.slice()
            .sort(getComparator(order, orderBy))
            .map((row) => (
              <TableRow
                data-testid={'row-click' + row.key}
                key={row.key}
                sx={rowStyle}
                onClick={() => rowClick(row)}
                onKeyUp={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    rowClick(row);
                  }
                }}
              >
                {row.data.map((obj: any, index: number) => (
                  <TableCell
                    tabIndex={0}
                    key={row.key ? row.key + index : index}
                  >
                    {obj}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default CcfTable;
